import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CytoscapeComponent from 'react-cytoscapejs';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import ErrorBoundary from '../components/ErrorBoundary';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Network,
  RefreshCw,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Filter,
  AlertTriangle,
  Users,
  CreditCard,
  Briefcase,
  Search,
  Download,
  Eye,
  Target,
  Activity,
  TrendingUp,
  Circle,
} from 'lucide-react';
import { toast } from 'sonner';

// Estilos para Cytoscape
const cytoscapeStylesheet = [
  {
    selector: 'node',
    style: {
      'label': 'data(label)',
      'text-valign': 'bottom',
      'text-halign': 'center',
      'font-size': '10px',
      'text-margin-y': '5px',
      'background-color': '#64748b',
      'width': '40px',
      'height': '40px',
      'border-width': '2px',
      'border-color': '#475569',
    }
  },
  {
    selector: 'node[type="cliente"]',
    style: {
      'background-color': '#8b5cf6',
      'border-color': '#7c3aed',
      'shape': 'ellipse',
    }
  },
  {
    selector: 'node[type="cuenta"]',
    style: {
      'background-color': '#3b82f6',
      'border-color': '#2563eb',
      'shape': 'round-rectangle',
    }
  },
  {
    selector: 'node[type="alerta"]',
    style: {
      'background-color': '#ef4444',
      'border-color': '#dc2626',
      'shape': 'diamond',
    }
  },
  {
    selector: 'node[type="caso"]',
    style: {
      'background-color': '#22c55e',
      'border-color': '#16a34a',
      'shape': 'hexagon',
    }
  },
  {
    selector: 'node:selected',
    style: {
      'border-width': '4px',
      'border-color': '#f59e0b',
      'background-color': '#fbbf24',
    }
  },
  {
    selector: 'edge',
    style: {
      'width': 2,
      'line-color': '#94a3b8',
      'target-arrow-color': '#94a3b8',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      'label': 'data(label)',
      'font-size': '8px',
      'text-rotation': 'autorotate',
      'text-margin-y': '-10px',
    }
  },
  {
    selector: 'edge[type="transferencia"]',
    style: {
      'line-color': '#22c55e',
      'target-arrow-color': '#22c55e',
      'width': 3,
    }
  },
  {
    selector: 'edge:selected',
    style: {
      'line-color': '#f59e0b',
      'target-arrow-color': '#f59e0b',
      'width': 4,
    }
  },
];

const NODE_TYPE_COLORS = {
  cliente: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Users },
  cuenta: { bg: 'bg-blue-100', text: 'text-blue-700', icon: CreditCard },
  alerta: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertTriangle },
  caso: { bg: 'bg-green-100', text: 'text-green-700', icon: Briefcase },
};

export default function NetworkAnalysis() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cyRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [networkData, setNetworkData] = useState({ nodes: [], edges: [] });
  const [stats, setStats] = useState(null);
  const [patterns, setPatterns] = useState(null);
  
  // Filters
  const [filterType, setFilterType] = useState('full');
  const [searchId, setSearchId] = useState('');
  const [montoMinimo, setMontoMinimo] = useState(0);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [nodeLimit, setNodeLimit] = useState(200);
  
  // Selected node details
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodeDialog, setShowNodeDialog] = useState(false);
  
  // Layout
  const [currentLayout, setCurrentLayout] = useState('cose');
  
  // Initial load based on URL params
  useEffect(() => {
    const casoId = searchParams.get('caso');
    const cuit = searchParams.get('cuit');
    
    if (casoId) {
      setFilterType('case');
      setSearchId(casoId);
      loadNetworkFromCase(casoId);
    } else if (cuit) {
      setFilterType('client');
      setSearchId(cuit);
      loadNetworkFromClient(cuit);
    } else {
      loadFullNetwork();
    }
  }, [searchParams]);

  const loadNetworkFromCase = async (casoId) => {
    setLoading(true);
    try {
      const res = await api.get(`/network/case/${casoId}`);
      setNetworkData(res.data);
      setStats(res.data.stats);
      toast.success(`Red del caso cargada: ${res.data.stats?.total_nodos || 0} nodos`);
    } catch (error) {
      toast.error('Error al cargar la red del caso');
    } finally {
      setLoading(false);
    }
  };

  const loadNetworkFromClient = async (cuit, depth = 2) => {
    setLoading(true);
    try {
      const res = await api.get(`/network/client/${cuit}?depth=${depth}`);
      setNetworkData(res.data);
      setStats(res.data.stats);
      toast.success(`Red del cliente cargada: ${res.data.stats?.total_nodos || 0} nodos`);
    } catch (error) {
      toast.error('Error al cargar la red del cliente');
    } finally {
      setLoading(false);
    }
  };

  const loadFullNetwork = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fechaInicio) params.append('fecha_inicio', fechaInicio);
      if (fechaFin) params.append('fecha_fin', fechaFin);
      if (montoMinimo > 0) params.append('monto_minimo', montoMinimo);
      params.append('limit', nodeLimit);
      
      const res = await api.get(`/network/full?${params.toString()}`);
      setNetworkData(res.data);
      setStats(res.data.stats);
      toast.success(`Red cargada: ${res.data.stats?.total_nodos || 0} nodos, ${res.data.stats?.total_conexiones || 0} conexiones`);
    } catch (error) {
      toast.error('Error al cargar la red');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (filterType === 'case' && searchId) {
      loadNetworkFromCase(searchId);
    } else if (filterType === 'client' && searchId) {
      loadNetworkFromClient(searchId);
    } else {
      loadFullNetwork();
    }
  };

  const detectPatterns = async () => {
    if (networkData.nodes.length === 0) {
      toast.error('Primero carga una red para analizar');
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.post('/network/detect-patterns', {
        nodes: networkData.nodes,
        edges: networkData.edges
      });
      setPatterns(res.data);
      toast.success(`Análisis completado. Score de riesgo: ${res.data.risk_score}`);
    } catch (error) {
      toast.error('Error al detectar patrones');
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = useCallback((event) => {
    const node = event.target;
    if (node.isNode()) {
      setSelectedNode({
        id: node.id(),
        label: node.data('label'),
        type: node.data('type'),
        data: node.data()
      });
      setShowNodeDialog(true);
    }
  }, []);

  const applyLayout = (layoutName) => {
    if (cyRef.current) {
      setCurrentLayout(layoutName);
      const layout = cyRef.current.layout({
        name: layoutName,
        animate: true,
        animationDuration: 500,
        fit: true,
        padding: 50,
        nodeRepulsion: 8000,
        idealEdgeLength: 100,
      });
      layout.run();
    }
  };

  const zoomIn = () => {
    if (cyRef.current) {
      cyRef.current.zoom(cyRef.current.zoom() * 1.2);
    }
  };

  const zoomOut = () => {
    if (cyRef.current) {
      cyRef.current.zoom(cyRef.current.zoom() / 1.2);
    }
  };

  const fitGraph = () => {
    if (cyRef.current) {
      cyRef.current.fit(50);
    }
  };

  // Convert network data to Cytoscape format (sanitize to prevent key overwrites)
  const cytoscapeElements = React.useMemo(() => {
    const nodeIdSet = new Set();
    const elements = [];

    for (const node of (networkData.nodes || [])) {
      if (!node.id) continue;
      nodeIdSet.add(node.id);
      const { id, label, type, ...rest } = node.data || {};
      elements.push({
        data: {
          ...rest,
          id: node.id,
          label: node.label || String(node.id),
          type: node.type || 'default',
        }
      });
    }

    (networkData.edges || []).forEach((edge, idx) => {
      if (!edge.source || !edge.target) return;
      if (!nodeIdSet.has(edge.source) || !nodeIdSet.has(edge.target)) return;
      const { source, target, id: _eid, ...restData } = edge.data || {};
      elements.push({
        data: {
          ...restData,
          id: `edge_${idx}`,
          source: edge.source,
          target: edge.target,
          label: edge.label || '',
          type: edge.data?.tipo || 'default',
        }
      });
    });

    return elements;
  }, [networkData]);

  return (
    <div className="p-6 space-y-4 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Network className="text-purple-600" />
            Análisis de Redes
          </h1>
          <p className="text-slate-500 mt-1">
            Visualiza y analiza conexiones entre entidades para detectar patrones de fraude
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={detectPatterns} disabled={loading || networkData.nodes.length === 0}>
            <Target size={16} className="mr-2" />
            Detectar Patrones
          </Button>
          <Button variant="outline" onClick={handleSearch} disabled={loading}>
            <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Filters Card */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="w-40">
              <Label className="text-xs">Tipo de Búsqueda</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Red Completa</SelectItem>
                  <SelectItem value="case">Por Caso</SelectItem>
                  <SelectItem value="client">Por Cliente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {(filterType === 'case' || filterType === 'client') && (
              <div className="flex-1 min-w-48">
                <Label className="text-xs">{filterType === 'case' ? 'ID del Caso' : 'CUIT del Cliente'}</Label>
                <div className="flex gap-2">
                  <Input
                    value={searchId}
                    onChange={(e) => setSearchId(e.target.value)}
                    placeholder={filterType === 'case' ? 'CASO-...' : '20-12345678-9'}
                    className="bg-white"
                  />
                  <Button onClick={handleSearch} disabled={loading}>
                    <Search size={16} />
                  </Button>
                </div>
              </div>
            )}
            
            {filterType === 'full' && (
              <>
                <div className="w-36">
                  <Label className="text-xs">Monto Mínimo</Label>
                  <Input
                    type="number"
                    value={montoMinimo}
                    onChange={(e) => setMontoMinimo(Number(e.target.value))}
                    placeholder="0"
                    className="bg-white"
                  />
                </div>
                <div className="w-36">
                  <Label className="text-xs">Fecha Desde</Label>
                  <Input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className="bg-white"
                  />
                </div>
                <div className="w-36">
                  <Label className="text-xs">Fecha Hasta</Label>
                  <Input
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    className="bg-white"
                  />
                </div>
                <div className="w-28">
                  <Label className="text-xs">Límite</Label>
                  <Input
                    type="number"
                    value={nodeLimit}
                    onChange={(e) => setNodeLimit(Number(e.target.value))}
                    min={10}
                    max={1000}
                    className="bg-white"
                  />
                </div>
                <Button onClick={handleSearch} disabled={loading}>
                  <Filter size={16} className="mr-2" />
                  Filtrar
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-4 gap-4">
        {/* Main Graph Area */}
        <div className="col-span-3">
          <Card className="h-[600px] relative">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Grafo de Red</CardTitle>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={zoomIn}>
                  <ZoomIn size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={zoomOut}>
                  <ZoomOut size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={fitGraph}>
                  <Maximize2 size={16} />
                </Button>
                <Select value={currentLayout} onValueChange={applyLayout}>
                  <SelectTrigger className="w-32 h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cose">Force-Directed</SelectItem>
                    <SelectItem value="circle">Circular</SelectItem>
                    <SelectItem value="grid">Grid</SelectItem>
                    <SelectItem value="breadthfirst">Jerárquico</SelectItem>
                    <SelectItem value="concentric">Concéntrico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0 h-[520px]">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <RefreshCw className="animate-spin text-purple-600" size={40} />
                </div>
              ) : networkData?.nodes?.length > 0 || cytoscapeElements.length > 0 ? (
                <ErrorBoundary fallback={
                  <div className="flex flex-col items-center justify-center h-full text-red-500">
                    <AlertTriangle size={40} className="mb-4" />
                    <p>Error al renderizar la red</p>
                    <Button variant="outline" onClick={() => window.location.reload()} className="mt-4">
                      Recargar página
                    </Button>
                  </div>
                }>
                  <CytoscapeComponent
                    elements={cytoscapeElements}
                    stylesheet={cytoscapeStylesheet}
                    style={{ width: '100%', height: '100%' }}
                    cy={(cy) => {
                      cyRef.current = cy;
                      cy.on('tap', 'node', handleNodeClick);
                    }}
                    layout={{ name: currentLayout, animate: false }}
                  />
                </ErrorBoundary>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-slate-500">
                  <Network size={60} className="mb-4 text-slate-300" />
                  <p>No hay datos para mostrar</p>
                  <p className="text-sm">Usa los filtros para cargar una red</p>
                </div>
              )}
            </CardContent>
            
            {/* Legend */}
            <div className="absolute bottom-4 left-4 bg-white/90 p-3 rounded-lg shadow-sm">
              <p className="text-xs font-semibold mb-2">Leyenda</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {Object.entries(NODE_TYPE_COLORS).map(([type, config]) => {
                  const Icon = config.icon;
                  return (
                    <div key={type} className="flex items-center gap-1">
                      <Circle size={10} className={config.text} fill="currentColor" />
                      <span className="capitalize">{type}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>

        {/* Side Panel */}
        <div className="space-y-4">
          {/* Stats */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity size={16} />
                Estadísticas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Nodos:</span>
                <span className="font-semibold">{stats?.total_nodos || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Conexiones:</span>
                <span className="font-semibold">{stats?.total_conexiones || 0}</span>
              </div>
              {stats?.volumen_total && (
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Volumen Total:</span>
                  <span className="font-semibold text-green-600">
                    {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(stats.volumen_total)}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Patterns Detected */}
          {patterns && (
            <Card className={patterns.risk_score > 50 ? 'border-red-300' : ''}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Target size={16} className={patterns.risk_score > 50 ? 'text-red-500' : ''} />
                  Patrones Detectados
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Score de Riesgo:</span>
                  <Badge className={
                    patterns.risk_score > 100 ? 'bg-red-500' :
                    patterns.risk_score > 50 ? 'bg-amber-500' : 'bg-green-500'
                  }>
                    {patterns.risk_score}
                  </Badge>
                </div>
                
                {patterns.summary.high_centrality_count > 0 && (
                  <div className="text-sm">
                    <div className="flex items-center gap-1 text-amber-600">
                      <TrendingUp size={14} />
                      <span>{patterns.summary.high_centrality_count} nodos hub</span>
                    </div>
                    <p className="text-xs text-slate-500 ml-5">Alta centralidad detectada</p>
                  </div>
                )}
                
                {patterns.summary.potential_cycles_count > 0 && (
                  <div className="text-sm">
                    <div className="flex items-center gap-1 text-red-600">
                      <AlertTriangle size={14} />
                      <span>{patterns.summary.potential_cycles_count} posibles ciclos</span>
                    </div>
                    <p className="text-xs text-slate-500 ml-5">Dinero circular detectado</p>
                  </div>
                )}
                
                {patterns.summary.high_volume_count > 0 && (
                  <div className="text-sm">
                    <div className="flex items-center gap-1 text-purple-600">
                      <Activity size={14} />
                      <span>{patterns.summary.high_volume_count} transferencias altas</span>
                    </div>
                    <p className="text-xs text-slate-500 ml-5">&gt; $1M por operación</p>
                  </div>
                )}
                
                {patterns.risk_score === 0 && (
                  <p className="text-sm text-green-600">✓ Sin patrones sospechosos</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Acciones</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                size="sm"
                onClick={() => {
                  if (cyRef.current) {
                    const png = cyRef.current.png({ scale: 2, bg: 'white' });
                    const link = document.createElement('a');
                    link.href = png;
                    link.download = 'network-analysis.png';
                    link.click();
                    toast.success('Imagen descargada');
                  }
                }}
              >
                <Download size={14} className="mr-2" />
                Exportar Imagen
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                size="sm"
                onClick={() => {
                  const data = JSON.stringify(networkData, null, 2);
                  const blob = new Blob([data], { type: 'application/json' });
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = 'network-data.json';
                  link.click();
                  toast.success('Datos exportados');
                }}
              >
                <Download size={14} className="mr-2" />
                Exportar JSON
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Node Detail Dialog */}
      <Dialog open={showNodeDialog} onOpenChange={setShowNodeDialog}>
        <DialogContent style={{ backgroundColor: '#ffffff' }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedNode && NODE_TYPE_COLORS[selectedNode.type] && (
                <>
                  {React.createElement(NODE_TYPE_COLORS[selectedNode.type].icon, { size: 20 })}
                  <span className="capitalize">{selectedNode.type}</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedNode && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500">Etiqueta</Label>
                <p className="font-medium">{selectedNode.label}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">ID</Label>
                <p className="font-mono text-sm">{selectedNode.id}</p>
              </div>
              {selectedNode.data && Object.entries(selectedNode.data).map(([key, value]) => {
                if (key === 'label' || key === 'type' || key === 'id') return null;
                return (
                  <div key={key}>
                    <Label className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</Label>
                    <p className="text-sm">
                      {typeof value === 'number' && (key.includes('monto') || key.includes('volumen') || key.includes('importe') || key.includes('saldo'))
                        ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(value)
                        : String(value)}
                    </p>
                  </div>
                );
              })}
              
              <div className="flex gap-2 pt-2">
                {selectedNode.type === 'cliente' && selectedNode.data?.cuit && (
                  <Button 
                    size="sm" 
                    onClick={() => navigate(`/m/clientes/padron/${selectedNode.data.cuit}`)}
                  >
                    <Eye size={14} className="mr-1" />
                    Ver Cliente
                  </Button>
                )}
                {selectedNode.type === 'alerta' && selectedNode.data?.id && (
                  <Button 
                    size="sm" 
                    onClick={() => navigate(`/m/monitoreo-transaccional/alerts/${selectedNode.data.id}`)}
                  >
                    <Eye size={14} className="mr-1" />
                    Ver Alerta
                  </Button>
                )}
                {selectedNode.data?.caso_id && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => navigate(`/m/gestion-de-casos/cases/${selectedNode.data.caso_id}`)}
                  >
                    <Briefcase size={14} className="mr-1" />
                    Ver Caso
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
